import { WriteTransaction } from 'replicache'

import {
  RichTextBlock,
  BlockRelationship,
  blockRelationshipBaseOps,
} from '@basehub/data/replicache/index'
import {
  flattenTipTapNodes,
  Node,
  jsonToYDocBase64State,
  richTextExtensionsForTransformations,
  stateToJson,
  BaseHubBlock,
  BaseHubInlineBlock,
} from '@basehub/tiptap-api-utils'
import { invariant } from '@epic-web/invariant'
import { ensureBaseHubTransaction } from '@basehub/data/replicache/tx/iso'
import { AtLeast } from '@basehub/utils/tree/index'

export async function resolveRichTextCustomChildBlocks(
  tx: WriteTransaction,
  {
    state,
    children,
    constraints,
  }: {
    state: RichTextBlock['value']['state']
    children: BlockRelationship[]
    constraints: RichTextBlock['constraints']
  }
): Promise<{
  children: BlockRelationship[]
  json: any
  livingChildren: Set<string>
  childrenMissing: BlockRelationship[]
}> {
  ensureBaseHubTransaction(tx)
  const json = stateToJson(state, { cacheKey: JSON.stringify(state) })

  /**
   * This function does one major task:
   * It keeps the rich text children list on sync with the content,
   * so, if an instance gets removed it should delete it from the children list.
   * ---
   * In order to do that, we:
   *
   * 1. Receive the children list and extract those component blocks from it.
   *    We do this because we want rich texts to be able to have components as children,
   *    but we don't want those components to be in the content (at least for now).
   *    So we keep them alive even if they are not in the content.
   *
   * 2. Iterate recursively through JSON and resolve block instances embedded in the rich text content (json)
   *    "resolve" means:
   *
   *    a. Add instances that are in the json to `children`.
   *    b. Remove (and delete) instances that are in `children` but no longer in the json.
   *       IMPORTANT: We don't delete instances yet this cause it'll break undo/redo in the rich text editor. we'll need to find a solution for this in the future.
   */

  const livingChildren = new Set<string>()
  const childrenBySourceId = new Map<string, BlockRelationship>()
  const childrenByRegularId = new Map<string, BlockRelationship>()
  const childrenMissing: BlockRelationship[] = []

  children.forEach((c) => {
    if (c.sourceBlockId) {
      childrenBySourceId.set(c.sourceBlockId, c)
    }
    childrenByRegularId.set(c.blockId, c)
  })

  const flattenNodes = flattenTipTapNodes(json.content as unknown as Node[])

  const resolveChildrenFromNodes = flattenNodes.map(async (node) => {
    const bshbMark = node.marks
      ? node.marks.find((mark) => mark.type === BaseHubInlineBlock.name)
      : null
    if (
      (node.type === BaseHubBlock.name && node.attrs?.id) ||
      (bshbMark && bshbMark.attrs?.id)
    ) {
      const id = bshbMark ? bshbMark.attrs?.id : node.attrs?.id
      let match = childrenBySourceId.get(id) || childrenByRegularId.get(id)

      // commenting this invariant out as we HAD several bugs in the system that are causing this to fail
      // hopefully, these bugs are resolved by now, but some trash still remains.

      // invariant(
      //   block && block.id,
      //   `BSHB: block not found when trying to resolve tiptap node ${node.attrs?.id}`
      // )
      // invariant(
      //   isInstanceBlock(block),
      //   `BSHB: tiptap custom nodes can only be instance type but found ${block}`
      // )

      if (!match) {
        match = await blockRelationshipBaseOps.get(tx, id)
        invariant(
          match,
          `BSHB: block not found when trying to resolve tiptap node ${id}`
        )
        childrenMissing.push(match)
      }
      livingChildren.add(match.id)
    }
  })

  constraints.allowedComponents?.forEach((c) => {
    const match = childrenBySourceId.get(c) || childrenByRegularId.get(c)
    if (!match) return
    livingChildren.add(match.id)
  })

  await Promise.all([...resolveChildrenFromNodes])

  return { children, json, livingChildren, childrenMissing }
}

/**
 * Imagine I duplicate a rich text block, and so I create new instances of each of the children.
 * This function will update the rich text state to target the new children.
 */
export function updateRichTextStateBasedOnNewChildren({
  state,
  newChildrenMap,
}: {
  state: RichTextBlock['value']['state']
  /** The key refers to the source block's id that was duplicated */
  newChildrenMap: Map<
    /** sourceBlockId || id */
    string,
    /** Doesn't need to be sourceBlockId, as this will be a new block */
    AtLeast<BlockRelationship, 'blockId' | 'sourceBlockId'>
  >
}) {
  const json = stateToJson(state)

  const livingChildren = new Set<string>()

  function processNode(node: Node) {
    if (BaseHubBlock.name === node.type) {
      invariant(node.attrs?.id, `rich text custom block has no id`)
      const newChild = newChildrenMap.get(node.attrs.id)
      invariant(
        newChild,
        `could not find new child for rich text custom block ${node.attrs.id}`
      )
      node.attrs.id = newChild.sourceBlockId || newChild.blockId
      livingChildren.add(newChild.blockId)
    } else if (node.marks) {
      node.marks = node.marks.map((mark) => {
        if (mark.type === BaseHubInlineBlock.name) {
          invariant(mark.attrs?.id, `rich text custom block has no id`)
          const newChild = newChildrenMap.get(mark.attrs.id)
          invariant(
            newChild,
            `could not find new child for rich text custom block ${mark.attrs.id}`
          )

          livingChildren.add(newChild.blockId)

          return { ...mark, attrs: { ...mark.attrs, id: newChild.id } }
        }
        return mark
      })
    }

    if (node.content) {
      const processed = node.content.map(processNode)
      node.content = processed
    }

    return node
  }

  const updatedJson = processNode(json as any)

  return {
    state: jsonToYDocBase64State(
      updatedJson as any,
      richTextExtensionsForTransformations
    ),
    livingChildren,
  }
}
